/**
 * Flip Page Bar + Page Selector (Chris, 2026-10-28).
 *
 * "We are really using the screen space badly." Flip Page Bar (a per-user
 * setting, db/123) hides the row of page pills; a "Page Selector" button beside
 * the NYMR Reports title then opens a panel of every page the user may see, in
 * groups. In the panel the user can pin ONE group to the header (its pill opens
 * the panel showing just that group) and mark ONE page "1st" — the page the
 * site opens on. The panel appears instantly, closes on Escape, an outside
 * click or a page change. Pages and groups come from src/navPages.ts.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { NAV_GROUPS, onPage, visibleGroupPages, type NavGroupKey, type NavMe } from '../navPages'

export interface NavPrefs {
  flip_page_bar: boolean
  pinned_group: NavGroupKey | null
  first_page: string | null
}

const DEFAULT_PREFS: NavPrefs = { flip_page_bar: false, pinned_group: null, first_page: null }

export interface NavPrefsState {
  prefs: NavPrefs
  /** True once the user's settings are known (from this browser's copy or the server). */
  loaded: boolean
  update: (patch: Partial<NavPrefs>) => void
}

const cacheKey = (upn: string) => `nymr-nav-prefs:${upn.toLowerCase()}`

function readCache(upn: string): NavPrefs | null {
  try {
    const raw = globalThis.localStorage?.getItem(cacheKey(upn))
    return raw ? { ...DEFAULT_PREFS, ...(JSON.parse(raw) as Partial<NavPrefs>) } : null
  } catch { return null }
}

function writeCache(upn: string, p: NavPrefs) {
  try { globalThis.localStorage?.setItem(cacheKey(upn), JSON.stringify(p)) } catch { /* private window — the server copy still holds */ }
}

/**
 * The user's navigation settings. The server (app.UserNavPrefs) is the record;
 * this browser keeps a copy so the page bar is right on first paint instead of
 * flashing open and then folding away.
 */
export function useNavPrefs(upn: string | null): NavPrefsState {
  const [prefs, setPrefs] = useState<NavPrefs>(() => (upn && readCache(upn)) || DEFAULT_PREFS)
  const [loaded, setLoaded] = useState<boolean>(() => !!(upn && readCache(upn)))
  const latest = useRef(prefs)
  latest.current = prefs

  useEffect(() => {
    if (!upn) return
    let cancelled = false
    void (async () => {
      try {
        const res = await fetch('/api/nav-prefs')
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const p = { ...DEFAULT_PREFS, ...((await res.json()) as Partial<NavPrefs>) }
        if (cancelled) return
        setPrefs(p)
        writeCache(upn, p)
      } catch (e) {
        console.warn('[nav-prefs] could not load; using defaults', e)
      } finally {
        if (!cancelled) setLoaded(true)
      }
    })()
    return () => { cancelled = true }
  }, [upn])

  const update = useCallback((patch: Partial<NavPrefs>) => {
    const next = { ...latest.current, ...patch }
    setPrefs(next)
    if (!upn) return
    writeCache(upn, next)
    void fetch('/api/nav-prefs', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(next),
    }).then((r) => { if (!r.ok) console.warn('[nav-prefs] save failed', r.status) })
      .catch((e) => console.warn('[nav-prefs] save failed', e))
  }, [upn])

  return { prefs, loaded, update }
}

/** The header toggle, left of the Email Digest button. */
export function FlipPageBarButton({ nav }: { nav: NavPrefsState }) {
  const on = nav.prefs.flip_page_bar
  return (
    <button
      type="button"
      className={'hdr-btn' + (on ? ' hdr-btn--on' : '')}
      aria-pressed={on}
      onClick={() => nav.update({ flip_page_bar: !on })}
      title={on
        ? 'The page bar is folded into the Page Selector — click to show the full row of pages again'
        : 'Fold the row of pages into a Page Selector button beside the title'}
    >
      ⇅ Flip Page Bar
    </button>
  )
}

/** "Page Selector" and the pinned group's pill, beside the NYMR Reports title. */
export function PageSelectorControls({ me, nav }: { me: NavMe; nav: NavPrefsState }) {
  const { pathname } = useLocation()
  const [open, setOpen] = useState<'all' | NavGroupKey | null>(null)
  const wrap = useRef<HTMLDivElement>(null)

  // Close on a page change, Escape, or a click outside.
  useEffect(() => { setOpen(null) }, [pathname])
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(null) }
    const onDown = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(null) }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown) }
  }, [open])

  const groups = NAV_GROUPS
    .map((g) => ({ g, pages: visibleGroupPages(g, me) }))
    .filter((x) => x.pages.length > 0)
  const pinned = groups.find((x) => x.g.key === nav.prefs.pinned_group) ?? null
  const shown = open === 'all' ? groups : groups.filter((x) => x.g.key === open)
  const here = groups.flatMap((x) => x.pages).find((p) => onPage(pathname, p.to))

  return (
    <div className="page-selector" ref={wrap}>
      <button
        type="button"
        className={'hdr-btn hdr-btn--selector' + (open === 'all' ? ' hdr-btn--on' : '')}
        aria-expanded={open === 'all'}
        aria-haspopup="dialog"
        onClick={() => setOpen(open === 'all' ? null : 'all')}
        title={here ? `On ${here.label} — choose another page` : 'Choose a page'}
      >
        ☰ Page Selector{here ? <span className="page-selector__here"> · {here.label}</span> : null}
      </button>
      {pinned && (
        <button
          type="button"
          className={'hdr-btn hdr-btn--group' + (open === pinned.g.key ? ' hdr-btn--on' : '')
            + (pinned.pages.some((p) => onPage(pathname, p.to)) ? ' hdr-btn--current' : '')}
          aria-expanded={open === pinned.g.key}
          aria-haspopup="dialog"
          onClick={() => setOpen(open === pinned.g.key ? null : pinned.g.key)}
          title={`Your pinned group — ${pinned.pages.map((p) => p.label).join(', ')}`}
        >
          📌 {pinned.g.title}
        </button>
      )}
      {open && (
        <div className={'page-selector__panel' + (open !== 'all' ? ' page-selector__panel--one' : '')} role="dialog" aria-label={open === 'all' ? 'Page Selector' : `${pinned?.g.title ?? ''} pages`}>
          <div className={'page-selector__grid' + (open !== 'all' ? ' page-selector__grid--one' : '')}>
            {shown.map(({ g, pages }) => {
              const isPinned = nav.prefs.pinned_group === g.key
              return (
                <section key={g.key} className={'page-group' + (isPinned ? ' page-group--pinned' : '')}>
                  <header className="page-group__head">
                    <h2>{g.title}</h2>
                    <button
                      type="button"
                      className={'page-group__pin' + (isPinned ? ' is-on' : '')}
                      aria-pressed={isPinned}
                      onClick={() => nav.update({ pinned_group: isPinned ? null : g.key })}
                      title={isPinned ? `Unpin ${g.title} from the header` : `Pin ${g.title} to the header (one group at a time)`}
                    >
                      📌 {isPinned ? 'Pinned' : 'Pin'}
                    </button>
                  </header>
                  <ul className="page-group__list">
                    {pages.map((p) => {
                      const isFirst = nav.prefs.first_page === p.to
                      return (
                        <li key={p.to} className={'page-block' + (onPage(pathname, p.to) ? ' is-here' : '')}>
                          <NavLink to={p.to} className="page-block__link" onClick={() => setOpen(null)}>
                            {p.label}
                          </NavLink>
                          <button
                            type="button"
                            className={'page-block__first' + (isFirst ? ' is-on' : '')}
                            aria-pressed={isFirst}
                            onClick={() => nav.update({ first_page: isFirst ? null : p.to })}
                            title={isFirst ? `${p.label} is the page the site opens on — click to clear` : `Open the site on ${p.label}`}
                          >
                            1st
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </section>
              )
            })}
          </div>
          {open === 'all' && (
            <p className="page-selector__foot">
              📌 pins one group to the header · <strong>1st</strong> is the page the site opens on
            </p>
          )}
        </div>
      )}
    </div>
  )
}
